// SPDX-License-Identifier: AGPL-3.0-or-later

import {usePrefersReducedMotion} from '@app/features/ui/hooks/usePrefersReducedMotion';
import {flxElementClassName} from '@app/lib/react';
import {animate} from 'animejs';
import {type ReactNode, useLayoutEffect, useRef} from 'react';

export const AuthSurfaceMotionKind = Object.freeze({
	FULLSCREEN: 'fullscreen',
	CARD: 'card',
} as const);

export type AuthSurfaceMotionKind = (typeof AuthSurfaceMotionKind)[keyof typeof AuthSurfaceMotionKind];

interface AuthSurfaceMotionState {
	translateY: number;
	scale: number;
	duration: number;
}

interface AuthSurfaceTransitionProps {
	surfaceKey: string;
	motionKind: AuthSurfaceMotionKind;
	suppressed: boolean;
	className: string;
	children: ReactNode;
}

const AUTH_SURFACE_MOTION: Record<AuthSurfaceMotionKind, AuthSurfaceMotionState> = {
	fullscreen: {translateY: 28, scale: 1, duration: 420},
	card: {translateY: 10, scale: 0.995, duration: 260},
};

function setSurfaceFinalStyle(surface: HTMLElement): void {
	surface.style.opacity = '1';
	surface.style.transform = 'translateY(0px) scale(1)';
}

export function AuthSurfaceTransition({
	surfaceKey,
	motionKind,
	suppressed,
	className,
	children,
}: AuthSurfaceTransitionProps) {
	const surfaceRef = useRef<HTMLElement | null>(null);
	const mountedSurfaceKeyRef = useRef<string | null>(null);
	const prefersReducedMotion = usePrefersReducedMotion();
	useLayoutEffect(() => {
		const surface = surfaceRef.current;
		if (surface == null) return;
		if (suppressed) {
			setSurfaceFinalStyle(surface);
			return;
		}
		const mountedSurfaceKey = mountedSurfaceKeyRef.current;
		mountedSurfaceKeyRef.current = surfaceKey;
		if (mountedSurfaceKey == null || mountedSurfaceKey === surfaceKey || prefersReducedMotion) {
			setSurfaceFinalStyle(surface);
			return;
		}
		const motionState = AUTH_SURFACE_MOTION[motionKind];
		const animation = animate(surface, {
			opacity: [0, 1],
			translateY: [motionState.translateY, 0],
			scale: [motionState.scale, 1],
			duration: motionState.duration,
			ease: 'out(4)',
		});
		return () => {
			animation.revert();
		};
	}, [motionKind, prefersReducedMotion, suppressed, surfaceKey]);
	return (
		<flx-auth-surface-transition
			ref={surfaceRef}
			className={flxElementClassName(className)}
			data-flx="auth.flow.auth-surface-transition.surface"
		>
			{children}
		</flx-auth-surface-transition>
	);
}
